import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, ScrollRestoration, useLocation } from "react-router-dom";
import { AppShell } from "@/components/shell/AppShell";
import { Omnibox } from "@/features/search/Omnibox";
import { useSignOut } from "@/context/AuthProvider";
import { useRbac } from "@/context/RbacContext";

/**
 * Wraps every route in the AppShell and owns the global omnibox. The Cmd-K (⌘K / Ctrl-K) shortcut and
 * the shell's search button both open the same overlay; sign-out is plumbed from the auth layer to the
 * user menu. The shortcut is only armed for users who can actually search (search:read), and it ignores
 * keystrokes while typing in another field so it never hijacks normal input.
 */
export function RootLayout() {
  const signOut = useSignOut();
  const { hasPermission } = useRbac();
  const canSearch = hasPermission("search:read");
  const [omniboxOpen, setOmniboxOpen] = useState(false);

  const openOmnibox = useCallback(() => setOmniboxOpen(true), []);
  const closeOmnibox = useCallback(() => setOmniboxOpen(false), []);

  // On client navigation, move focus to the main region so SR users are told the page changed and
  // keyboard users aren't stranded on an unmounted control. Skip the initial render.
  const { pathname } = useLocation();
  const firstNav = useRef(true);
  useEffect(() => {
    if (firstNav.current) {
      firstNav.current = false;
      return;
    }
    document.getElementById("main")?.focus({ preventScroll: true });
  }, [pathname]);

  useEffect(() => {
    if (!canSearch) return;
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOmniboxOpen((open) => !open);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canSearch]);

  return (
    <>
      <ScrollRestoration />
      <AppShell {...(canSearch ? { onOpenOmnibox: openOmnibox } : {})} onSignOut={signOut}>
        <Outlet />
      </AppShell>
      {canSearch && <Omnibox open={omniboxOpen} onClose={closeOmnibox} />}
    </>
  );
}
